import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import {
  byPresentationOrder,
  type PublicAskedQuestion,
  type PublicQuizState,
  type ServerEvent,
} from '@floorpoll/shared';
import { CodeCells } from '../../components/CodeCells.js';
import { ResultBars } from '../../components/ResultBars.js';
import { VendorMark } from '../../components/VendorMark.js';
import { apiRequest } from '../../lib/api.js';
import { useFitToHeight } from '../../lib/fitToHeight.js';
import { questionGrid } from '../../lib/questionGrid.js';
import { themeStyle } from '../../lib/theme.js';
import { useCountdown } from '../../lib/useCountdown.js';
import { useLiveQuiz } from '../../lib/useLiveQuiz.js';
import { useNoIndex } from '../../lib/useNoIndex.js';

interface ScreenPayload {
  state: PublicQuizState;
  joinCode: string;
  /** #118 — approved questions for this run, in the moderator's order. */
  questions: PublicAskedQuestion[];
}

/** Story #22 — the public big screen. Read from the back of a room. */
export function BigScreen() {
  useNoIndex();
  const { quizId = '' } = useParams();
  const [state, setState] = useState<PublicQuizState | null>(null);
  const [joinCode, setJoinCode] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  // #118 — the approved feed, so the projector can print the question the
  // presenter puts up. Kept beside the state rather than on it: a projector
  // reads the Q&A, but the payload's own shape is the poll's.
  const [questions, setQuestions] = useState<PublicAskedQuestion[]>([]);
  const [error, setError] = useState<string | null>(null);
  const joinUrlRef = useRef('');

  const resync = useCallback(async () => {
    try {
      const payload = await apiRequest<ScreenPayload>(`/public/screen/${quizId}`, {
        anonymous: true,
      });
      setState(payload.state);
      setJoinCode(payload.joinCode);
      setQuestions(payload.questions ?? []);
      setError(null);
    } catch {
      setError("This screen isn't available. Open it again from the presenter controls.");
    }
  }, [quizId]);

  const onEvent = useCallback((event: ServerEvent) => {
    // #118 — the approved feed, which is its own state rather than part of the
    // payload below. The projector only ever receives the pushes a public
    // surface may see: an approval carries the question, a rejection never
    // arrives at all.
    switch (event.type) {
      case 'state':
        // Broadcast when a run starts, and a new run has its own questions —
        // the server filters the feed by session, so the right client state is
        // an empty one. A reconnect re-reads the real list over HTTP instead
        // (onResync), so nothing is lost by clearing here.
        setQuestions([]);
        break;
      case 'questionModerated':
        if (event.question) {
          const approved = event.question;
          setQuestions((current) =>
            [
              ...current.filter((q) => q.askedQuestionId !== approved.askedQuestionId),
              approved,
            ].sort(byPresentationOrder),
          );
        }
        break;
      case 'questionLiked':
        setQuestions((current) =>
          current.map((q) =>
            q.askedQuestionId === event.askedQuestionId ? { ...q, likes: event.likes } : q,
          ),
        );
        break;
      case 'questionAnswered':
        setQuestions((current) =>
          current.map((q) =>
            q.askedQuestionId === event.askedQuestionId
              ? { ...q, answer: event.answer, answeredAt: event.answeredAt }
              : q,
          ),
        );
        break;
      default:
        break;
    }

    setState((current) => {
      if (!current) return current;
      switch (event.type) {
        case 'state':
          return event.state;
        case 'screen':
          return {
            ...current,
            currentScreenIndex: event.currentScreenIndex,
            screen: event.screen,
            ...(event.results ? { results: event.results } : { results: [] }),
            tallies: event.tallies,
            // Advancing resets both server-side; copied here because this
            // reducer merges a fixed field list off the event, and a hold left
            // standing would blank a screen nobody had held.
            resultsVisible: event.resultsVisible,
            votingClosed: event.votingClosed,
            // The new screen's clock (#77). The projector is the surface the
            // countdown is for, so an unmerged deadline here is the way this
            // story fails quietly: the room would see no clock at all.
            screenDeadlineAt: event.screenDeadlineAt,
            serverTime: event.serverTime,
          };
        case 'controls':
          // Ignore a toggle that raced a screen change, for the same reason a
          // vote is dropped: it describes the screen the room has left.
          if (event.currentScreenIndex !== current.currentScreenIndex) return current;
          return {
            ...current,
            resultsVisible: event.resultsVisible,
            votingClosed: event.votingClosed,
            // Exactly one of these is ever set: the server sends the numbers or
            // the tallies, never both. Clearing the other is what takes the
            // distribution off the projector on a hold and puts it back on a
            // reveal.
            results: event.results,
            tallies: event.tallies,
          };
        case 'vote':
          // Ignore a vote that raced a screen change; it describes the screen
          // the room has already moved past.
          return event.currentScreenIndex === current.currentScreenIndex
            ? { ...current, results: event.results }
            : current;
        case 'ended':
          return { ...current, status: 'draft', screen: null };
        // #118 — the projector is told which question is up, and nothing else
        // about the queue moves this payload. The feed itself lives in its own
        // state below, updated by the effect that watches the same events.
        case 'questionPresented':
          return { ...current, presentedAskedQuestionId: event.askedQuestionId };
        default:
          return current;
      }
    });
  }, []);

  const status = useLiveQuiz({ target: { quizId }, view: 'screen', onEvent, onResync: resync });

  const joinUrl = `${window.location.origin}/j/${joinCode}`;
  joinUrlRef.current = joinUrl;

  useEffect(() => {
    if (!joinCode) return;
    void QRCode.toDataURL(joinUrl, {
      width: 480,
      margin: 1,
      color: { dark: '#000000', light: '#ffffff' },
    }).then(setQrDataUrl);
  }, [joinCode, joinUrl]);

  if (error) {
    return (
      <div className="big-screen big-screen-empty">
        <h1>{error}</h1>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="big-screen big-screen-empty">
        <p>Loading…</p>
      </div>
    );
  }

  const style = themeStyle(state.theme);
  const isLive = state.status === 'live' && state.screen;

  return (
    // Exactly one viewport, never a page: a projector has no scrollbar anyone
    // can reach, so what does not fit is simply not seen. The header, a
    // question from the room and the vendor mark keep their size, and the
    // results take what is left — see BigScreenBody for how they fit it.
    <div className="big-screen big-screen-viewport" style={style}>
      <header className="big-screen-head">
        <div>
          {state.theme.logoUrl ? (
            <img src={state.theme.logoUrl} alt="" className="big-screen-logo" />
          ) : null}
          <div className="big-screen-title-row">
            <h1>{state.title}</h1>
            {isLive ? <span className="lamp">Live</span> : null}
          </div>
        </div>

        {isLive ? <BigScreenClock state={state} /> : null}

        {isLive ? (
          <div className="join-panel">
            {qrDataUrl ? <img src={qrDataUrl} alt={`QR code to join at ${joinUrl}`} /> : null}
            <div className="join-details">
              <p className="join-url">{joinUrl.replace(/^https?:\/\//, '')}</p>
              <CodeCells code={joinCode} size="lg" flip />
            </div>
          </div>
        ) : null}
      </header>

      {/* #118 — the question the presenter put up, above the ballot and
          bigger than it, because while a question is on screen it is what the
          room is looking at. Nothing renders when nothing is presented, so a
          poll not using Q&A looks exactly as it did before this shipped. */}
      {isLive ? (
        <BigScreenQuestion
          question={
            questions.find((q) => q.askedQuestionId === state.presentedAskedQuestionId) ?? null
          }
        />
      ) : null}

      {isLive && state.screen ? (
        <BigScreenBody state={state} />
      ) : (
        <main className="big-screen-body big-screen-empty">
          <h2>{state.status === 'live' ? 'Getting ready…' : 'The presentation has ended'}</h2>
          <p>Thanks for taking part.</p>
        </main>
      )}

      {/* #91 — the vendor mark, on the room's own screen. Last row of the
          column rather than a corner overlay: .big-screen-body takes flex: 1,
          so this clears the results grid at every questionColumns setting
          without knowing anything about it.

          The flag is the server's answer, from the entitlement stamped when
          this run started — a paid workspace is simply never sent a true. */}
      {state.vendorMark ? <VendorMark /> : null}

      {status !== 'live' ? (
        <p className="connection-pill" role="status">
          {status === 'connecting' ? 'Connecting…' : 'Reconnecting…'}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Story #77 — the clock, sized for the back of a room.
 *
 * Its own component so the ticking re-render stays off the results grid, which
 * animates its bars: re-rendering that four times a second would restart the
 * transition on every tick.
 */
function BigScreenClock({ state }: { state: PublicQuizState }) {
  const countdown = useCountdown(state.screenDeadlineAt, state.serverTime);
  if (!countdown) return null;

  return (
    <div className={`big-screen-clock${countdown.expired ? ' expired' : ''}`} role="timer">
      <span className="big-screen-clock-value">{countdown.label}</span>
      <span className="big-screen-clock-unit">
        {countdown.expired ? "time's up" : 'seconds left'}
      </span>
    </div>
  );
}

/**
 * The results grid.
 *
 * Two classes rather than one because columns and rows are independent axes:
 * rows drives how much height each question gets, columns how much width. The
 * single class this replaced was derived from the question count alone, which
 * was unambiguous only while every layout was a single column.
 */
function BigScreenBody({ state }: { state: PublicQuizState }) {
  const bodyRef = useRef<HTMLElement>(null);
  const swapRef = useRef<HTMLDivElement>(null);
  // The results fit the height the header and the vendor mark leave them. Sized
  // for a comfortable screen, a crowded one — three questions stacked, ten
  // answers, an image above the answers — is shrunk by the `--fit` multiplier
  // every size in here is written against, rather than running off the bottom
  // of the projector.
  useFitToHeight(bodyRef, swapRef, state.screen?.screenId);

  const screen = state.screen;
  if (!screen) return null;

  const { cols, rows } = questionGrid(state.theme.questionColumns, screen.questions.length);

  return (
    <main ref={bodyRef} className={`big-screen-body cols-${cols} rows-${rows}`}>
      {/* Keyed by screen so advancing plays the entrance animation. */}
      <div className="screen-swap" key={screen.screenId} ref={swapRef}>
        {state.resultsVisible ? (
          <ResultBars questions={state.results ?? []} large animated />
        ) : (
          <HeldResults state={state} />
        )}
      </div>
    </main>
  );
}

/**
 * Story #49 — the projector while the presenter holds the numbers back.
 *
 * A deliberate holding state, not an empty chart: the question and how many
 * have answered it, so the room can see the count climbing and knows the poll
 * is working, with nothing about which way it is going.
 *
 * There is nothing to hide client-side — `state.results` is absent because the
 * server never sent it. Falls back to the screen's own questions if `tallies`
 * has not landed yet, so a hold that arrives before the first payload still
 * paints the questions rather than an empty box.
 */
function HeldResults({ state }: { state: PublicQuizState }) {
  const tallies = state.tallies;
  const rows =
    tallies ??
    (state.screen?.questions ?? []).map((question) => ({
      questionId: question.questionId,
      text: question.text,
      totalVotes: 0,
    }));

  return (
    <div className="results results-large results-held">
      {rows.map((row) => (
        <div className="result-question" key={row.questionId}>
          <h3>{row.text}</h3>
          <p className="muted result-total">
            {row.totalVotes} {row.totalVotes === 1 ? 'vote in' : 'votes in'}
          </p>
          <p className="result-held-note" role="status">
            Results are hidden until the presenter reveals them.
          </p>
        </div>
      ))}
    </div>
  );
}

/**
 * The asked question currently on the projector (#118), or nothing.
 *
 * Plain text, never markup: this is a stranger's typing on a screen in front
 * of a room, and it has been through a moderator rather than a sanitiser.
 *
 * The like count rides along because it is the room's own signal about which
 * question mattered — the thing an audience gets from a Q&A that a hand in the
 * air does not give them.
 */
function BigScreenQuestion({ question }: { question: PublicAskedQuestion | null }) {
  if (!question) return null;

  return (
    <section className="big-screen-question" aria-live="polite">
      <p className="big-screen-question-label">From the room</p>
      <p className="big-screen-question-text">{question.content}</p>
      {question.likes > 0 ? (
        <p className="big-screen-question-likes">
          {question.likes} {question.likes === 1 ? 'person' : 'people'} asked for this
        </p>
      ) : null}
      {question.answer ? <p className="big-screen-question-answer">{question.answer}</p> : null}
    </section>
  );
}
